import { createRouter, createWebHistory } from 'vue-router'
import NProgress from 'nprogress/nprogress'
import { hasPermission, isAuthorized, siteClearance } from '@utils/clearanceHelpers'
import store from '@state/store'

import routes from './routes'

// Hide Nprogress spinner
NProgress.configure({ showSpinner: false })

const router = createRouter({
  routes,
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
})

// Before each route evaluates...
router.beforeEach(async (to, from, next) => {
  const { query, meta, params, name, path, fullPath, hash } = to
  const authorization = query.authorization
  const currentToken = store.state.auth.authToken

  if (authorization && authorization !== currentToken) {
    await store.dispatch('auth/sendTokenV2', authorization)
    const nextQuery = { ...query }
    delete nextQuery.authorization

    return next({ path, query: nextQuery, replace: true })
  }

  if (from.name !== null) NProgress.start()

  const actorRole = store.state.auth.actorRoles
  const actorRoleCode = actorRole?.code
  const actorRoleSite = actorRole?.site || null

  const isAdmin = siteClearance.admin.includes(actorRoleCode)
  const isDirector = siteClearance.director.includes(actorRoleCode)

  const routeSiteParamKey = params?.store ? 'store' : null
  const routeSiteParam = routeSiteParamKey ? String(params[routeSiteParamKey]) : null

  if (routeSiteParam && actorRoleSite && !isAdmin && routeSiteParam !== actorRoleSite) {
    if (name) {
      return next({
        name,
        params: { ...params, [routeSiteParamKey]: actorRoleSite },
        query,
        hash,
        replace: true,
      })
    }

    const fixedPath = path.replace(`/mx/${routeSiteParam}`, `/mx/${actorRoleSite}`)
    return next({ path: fixedPath, query, hash, replace: true })
  }

  if (isDirector && name === 'mexico-map') {
    return next({ path: `/mx/${actorRoleSite}/collaborators` })
  }

  const authRequired = to.matched.some((route) => route.meta.authRequired)
  if (!authRequired) return next()

  const { authorize, authorizeWithPermission } = meta
  const hasRoleRestriction = Array.isArray(authorize) && authorize.length > 0
  const hasPermissionRestriction = Boolean(authorizeWithPermission)

  if (hasRoleRestriction || hasPermissionRestriction) {
    const actorPermissions = store.state.auth.actorPermissions
    const hasRoleAccess = hasRoleRestriction ? isAuthorized(actorRole, authorize) : false
    const hasPermissionAccess = hasPermission(actorPermissions, authorizeWithPermission)

    if ((actorRole || actorPermissions) && !hasRoleAccess && !hasPermissionAccess) return redirectToHome()
  }

  if (store.getters['auth/loggedIn']) {
    return store
      .dispatch('auth/validate')
      .then((validUser) => (validUser ? next() : redirectToLogin()))
      .catch((e) => {
        console.warn(e)
        return redirectToLogin()
      })
  }

  return redirectToLogin()

  function redirectToHome() {
    const collaboratorId = store.state.auth.currentUser?.uuid

    if (siteClearance.collaboratorServices.includes(actorRoleCode)) {
      return next({ path: `/mx/${actorRoleSite}/collaborator-home` })
    }

    if (siteClearance.collaborator.includes(actorRoleCode) && collaboratorId) {
      return next({ path: `/mx/monthPlanning/${collaboratorId}` })
    }

    if (siteClearance.responsibleOperation.includes(actorRoleCode) || (siteClearance.responsible.includes(actorRoleCode) && actorRoleSite)) {
      return next({ path: `/mx/${actorRoleSite}/collaborators` })
    }

    return next({ path: '/', query: { redirectFrom: fullPath } })
  }

  function redirectToLogin() {
    return next({ name: 'login', query: { redirectFrom: fullPath } })
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  NProgress.done()
})

export default router
